import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useOnboardingStore } from "@/stores/onboarding";
import { trackFunnel } from "@/lib/analytics";

export const Route = createFileRoute("/onboarding/persona-setup")({
  component: PersonaSetupStep,
});

function PersonaSetupStep() {
  const navigate = useNavigate();
  const personas = useOnboardingStore((s) => s.personas);
  const otherSub = useOnboardingStore((s) => s.otherSub);
  const answers = useOnboardingStore((s) => s.personaAnswers);
  const setPersonaAnswers = useOnboardingStore((s) => s.setPersonaAnswers);
  const primary = personas[0] || "resident";

  function goNext(skipped = false) {
    trackFunnel(skipped ? "step_skipped" : "step_completed", "persona-setup");
    void navigate({ to: "/onboarding/alerts" });
  }

  return (
    <div className="flex flex-1 flex-col">
      <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-primary">
        Quick setup
      </p>
      <h1 className="font-display text-3xl font-bold">A few details</h1>
      <p className="mt-3 text-sm text-muted-foreground">
        All questions are optional. Skip any you prefer not to answer.
      </p>

      <div className="mt-6 space-y-4">
        {primary === "driver" && (
          <>
            <Field label="Vehicle type">
              <select
                className="h-11 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={String(answers.vehicleType || "")}
                onChange={(e) => setPersonaAnswers({ vehicleType: e.target.value })}
              >
                <option value="">Select</option>
                <option value="matatu">Matatu / PSV</option>
                <option value="private">Private car</option>
                <option value="pickup">Pickup / van</option>
                <option value="truck">Truck / lorry</option>
                <option value="ridehail">Ride-hail / delivery</option>
              </select>
            </Field>
            <Field label="Clearance">
              <select
                className="h-11 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={String(answers.clearance || "")}
                onChange={(e) => setPersonaAnswers({ clearance: e.target.value })}
              >
                <option value="">Select</option>
                <option value="low">Low</option>
                <option value="standard">Standard</option>
                <option value="high">High clearance / 4x4</option>
              </select>
            </Field>
            <Field label="Do you drive for work?">
              <select
                className="h-11 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={String(answers.driveForWork || "")}
                onChange={(e) => setPersonaAnswers({ driveForWork: e.target.value })}
              >
                <option value="">Select</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </Field>
            <Field label="Sacco / company (optional)">
              <Input
                value={String(answers.company || "")}
                onChange={(e) => setPersonaAnswers({ company: e.target.value })}
                placeholder="Optional"
              />
            </Field>
          </>
        )}

        {primary === "rider" && (
          <>
            <Field label="Vehicle">
              <select
                className="h-11 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={String(answers.riderVehicle || "")}
                onChange={(e) => setPersonaAnswers({ riderVehicle: e.target.value })}
              >
                <option value="">Select</option>
                <option value="boda">Boda boda</option>
                <option value="motorcycle">Motorcycle</option>
                <option value="bicycle">Bicycle</option>
              </select>
            </Field>
            <Field label="Shift pattern">
              <select
                className="h-11 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={String(answers.shift || "")}
                onChange={(e) => setPersonaAnswers({ shift: e.target.value })}
              >
                <option value="">Select</option>
                <option value="day">Day</option>
                <option value="night">Night</option>
                <option value="both">Both</option>
              </select>
            </Field>
            <Field label="Carry passengers or cargo?">
              <select
                className="h-11 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={String(answers.carry || "")}
                onChange={(e) => setPersonaAnswers({ carry: e.target.value })}
              >
                <option value="">Select</option>
                <option value="passengers">Passengers</option>
                <option value="cargo">Cargo</option>
                <option value="both">Both</option>
              </select>
            </Field>
          </>
        )}

        {primary === "resident" && (
          <>
            <Field label="Household size">
              <Input
                type="number"
                min={1}
                value={String(answers.householdSize || "")}
                onChange={(e) => setPersonaAnswers({ householdSize: e.target.value })}
              />
            </Field>
            <Field label="Children or elderly at home?">
              <select
                className="h-11 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={String(answers.vulnerable || "")}
                onChange={(e) => setPersonaAnswers({ vulnerable: e.target.value })}
              >
                <option value="">Select</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </Field>
            <Field label="Home type">
              <select
                className="h-11 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={String(answers.homeType || "")}
                onChange={(e) => setPersonaAnswers({ homeType: e.target.value })}
              >
                <option value="">Select</option>
                <option value="ground">Ground floor</option>
                <option value="upper">Upper floor</option>
                <option value="informal">Informal settlement / low-lying</option>
                <option value="unknown">Don&apos;t know</option>
              </select>
            </Field>
          </>
        )}

        {primary === "other" && (
          <Field label={`Details for ${otherSub || "your role"}`}>
            <Input
              value={String(answers.otherDetail || "")}
              onChange={(e) => setPersonaAnswers({ otherDetail: e.target.value })}
              placeholder="Optional notes"
            />
          </Field>
        )}
      </div>

      <div className="mt-auto space-y-2 pt-8">
        <Button className="w-full" onClick={() => goNext(false)}>
          Continue
        </Button>
        <Button variant="ghost" className="w-full" onClick={() => goNext(true)}>
          Skip for now
        </Button>
        <Button
          variant="outline"
          className="w-full"
          onClick={() => void navigate({ to: "/onboarding/persona" })}
        >
          Back
        </Button>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs font-semibold">{label}</Label>
      {children}
    </div>
  );
}
